import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Wifi, Battery, Search } from 'lucide-react';
import './App.css';
import Orb from './Orb';
import VariableProximity from './VariableProximity';
import ShinyText from './ShinyText';
import CircularGallery from './CircularGallery';

// macOS Menu Bar Component
const MenuBar = ({ onContactClick, onResumeClick }) => {
  const [time, setTime] = useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return {
      dayMonth: `${days[date.getDay()]} ${months[date.getMonth()]} ${date.getDate()}`,
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const { dayMonth, time: currentTime } = formatTime(time);

  return (
    <motion.div 
      className="menubar"
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="menubar-left">
        <div className="apple-logo">💫</div>
        <div className="menu-items">
          <span className="menu-item active">Ceren Erkayiran's Portfolio</span>
          <span className="menu-item" onClick={onContactClick}>Contact</span>
          <span className="menu-item" onClick={onResumeClick}>Resume</span>
        </div>
      </div>
      <div className="menubar-right">
        <div className="menu-icons">
          <Battery size={16} color="#111" />
          <Wifi size={16} color="#111" />
          <Search size={16} color="#111" />
        </div>
        <div className="datetime">
          <span className="day-month">{dayMonth}</span>
          <span className="time">{currentTime}</span>
        </div>
      </div>
    </motion.div>
  );
};

// macOS Window Component - Optimized for performance
const MacWindow = React.memo(({ title, children, initialPosition, onClose, isMinimized, onMinimize, onMaximize, width, height, windowType }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  const handleMaximize = useCallback(() => {
    setIsMaximized(!isMaximized);
    if (onMaximize) onMaximize();
  }, [isMaximized, onMaximize]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handleMinimize = useCallback(() => {
    if (onMinimize) onMinimize();
  }, [onMinimize]);

  // Create inline styles that will override CSS
  const windowStyles = {
    position: 'absolute', 
    ...initialPosition,
    width: width || 'auto',
    height: height || 'auto',
    // Use CSS custom properties for maximum control
    '--window-width': width || 'auto',
    '--window-height': height || 'auto',
  };

  // If width is specified, we need to ensure it overrides CSS min-width
  if (width) {
    windowStyles.minWidth = width;
    windowStyles.maxWidth = width;
    // Also set the CSS custom property
    windowStyles['--forced-width'] = width;
  }

  return (
    <motion.div
      drag={!isMaximized}
      dragMomentum={false}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: isMinimized ? 0.1 : 1, 
        opacity: isMinimized ? 0 : 1,
        y: isMinimized ? 500 : 0,
        width: isMaximized ? '100vw' : 'auto',
        height: isMaximized ? 'calc(100vh - 100px)' : 'auto',
        top: isMaximized ? '30px' : initialPosition.top,
        left: isMaximized ? '0px' : initialPosition.left
      }}
      exit={{ scale: 0, opacity: 0 }}
      className={`${windowType === 'about' ? 'about-window' : 'mac-window'} ${isMaximized ? 'maximized' : ''} ${width ? 'fixed-width' : ''}`}
      style={windowStyles}
      whileDrag={{ scale: 1.02, zIndex: 1000 }}
    >
      <div className={windowType === 'about' ? 'about-titlebar' : 'mac-titlebar'}>
        <div className="traffic-lights">
          <button className="traffic-light close" onClick={handleClose}>
            <span></span>
          </button>
          <button className="traffic-light minimize" onClick={handleMinimize}>
            <span></span>
          </button>
          <button className="traffic-light maximize" onClick={handleMaximize}>
            <span></span>
          </button>
        </div>
        <div className="window-title">{title}</div>
        <div className="titlebar-spacer"></div>
      </div>
      <div className="mac-window-content">
        {children}
      </div>
    </motion.div>
  );
});

// Desktop Icon Component - Simple and stable
const DesktopIcon = React.memo(({ iconId, icon, title, position, onClick, dragConstraints }) => {
  const handleClick = useCallback((e) => {
    onClick(e);
  }, [onClick]);

  return (
    <motion.div
      className="desktop-icon"
      data-icon-id={iconId}
      style={{ 
        position: 'absolute',
        top: position.top,
        left: position.left,
        cursor: 'grab',
        transform: 'translate3d(0, 0, 0)' // Hardware acceleration
      }}
      drag
      dragMomentum={false}
      dragElastic={0.1}
      whileTap={{ scale: 0.95 }}
      whileDrag={{ 
        scale: 1.05, 
        zIndex: 1000,
        cursor: 'grabbing'
      }}
      onClick={handleClick}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        delay: Math.random() * 0.2,
        type: "spring",
        stiffness: 300,
        damping: 20
      }}
    >
      <div className="icon-image" style={{ pointerEvents: 'none' }}>
        <MacIcon type={icon} size={64} />
      </div>
      <div className="icon-label" style={{ pointerEvents: 'none' }}>{title}</div>
    </motion.div>
  );
});

// macOS-style Icon Component using actual images
const MacIcon = ({ type, size = 48 }) => {
  const iconMap = {
    finder: `${process.env.PUBLIC_URL}/icons/finder.png`,
    file: `${process.env.PUBLIC_URL}/icons/file.png`,
    appstore: `${process.env.PUBLIC_URL}/icons/appstore.png`,
    contacts: `${process.env.PUBLIC_URL}/icons/contacts.png`,
    folderwork: `${process.env.PUBLIC_URL}/icons/folderwork.png`,
    notes: `${process.env.PUBLIC_URL}/icons/notes.png`,
    mail: `${process.env.PUBLIC_URL}/icons/mail.png`,
    foldercommon: `${process.env.PUBLIC_URL}/icons/foldercommon.png`,
    vscode: `${process.env.PUBLIC_URL}/icons/vscode.png`,
    terminal: `${process.env.PUBLIC_URL}/icons/terminal.png`,
    safari: `${process.env.PUBLIC_URL}/icons/safari.png`,
    calendar: `${process.env.PUBLIC_URL}/icons/calendar.png`,
    maps: `${process.env.PUBLIC_URL}/icons/maps.png`,
    txt: `${process.env.PUBLIC_URL}/icons/txt.png`,
    reminders: `${process.env.PUBLIC_URL}/icons/reminders.png`,
    launchpad: `${process.env.PUBLIC_URL}/icons/launchpad.png`,
    gta: `${process.env.PUBLIC_URL}/icons/gta-vicecity.png`,
    settings: `${process.env.PUBLIC_URL}/icons/settings.png`,
    document: `${process.env.PUBLIC_URL}/icons/file.png`,
    'written-file': `${process.env.PUBLIC_URL}/icons/written-file.png`,
    'grey-folder': `${process.env.PUBLIC_URL}/icons/grey-folder.png`,
    trash: `${process.env.PUBLIC_URL}/icons/trash.png`,
    player: `${process.env.PUBLIC_URL}/icons/player.png`
  };

  return (
    <div className="mac-icon-container" style={{ width: size, height: size }}>
      <img 
        src={iconMap[type]} 
        alt={type}
        className="mac-icon-image"
        style={{ width: size, height: size }}
        draggable={false}
      />
    </div>
  );
};

// Dock Component - Using inline styles for exact dimensions
const DockItem = ({ item, onIconClick, mouseX, baseItemSize = 48, magnification = 80, distance = 100 }) => {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const mouseDistance = useTransform(mouseX, (val) => {
    const rect = ref.current?.getBoundingClientRect() ?? { x: 0, width: baseItemSize };
    return val - rect.x - baseItemSize / 2;
  });

  // Calculate scale for the icon only
  const targetScale = useTransform(
    mouseDistance,
    [-distance, 0, distance],
    [1, magnification / baseItemSize, 1]
  );

  return (
    <motion.div
      ref={ref}
      className="dock-item"
      style={{ width: baseItemSize, height: baseItemSize, overflow: 'visible', zIndex: isHovered ? 2 : 1 }}
      onClick={(e) => onIconClick(item, e)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className="dock-icon"
        style={{ scale: targetScale }}
      >
        <img
          src={item.src}
          alt={item.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
          draggable={false}
        />
      </motion.div>
      <AnimatePresence>
        {isHovered && item.name && (
          <motion.div
            className="dock-label"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: -10 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
          >
            {item.name}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Dock = ({ onIconClick }) => {
  const mouseX = useMotionValue(Infinity);
  const isHovered = useMotionValue(0);
  
  const dockItems = [
    { id: 'finder', name: '', src: `${process.env.PUBLIC_URL}/icons/finder.png` },
    { id: 'launchpad', name: '', src: `${process.env.PUBLIC_URL}/icons/launchpad.png` },
    { id: 'safari', name: '', src: `${process.env.PUBLIC_URL}/icons/safari.png` },
    { id: 'maps', name: '', src: `${process.env.PUBLIC_URL}/icons/maps.png` },
    { id: 'mail', name: '', src: `${process.env.PUBLIC_URL}/icons/mail.png` },
    { id: 'photos', name: 'open me', src: `${process.env.PUBLIC_URL}/icons/photos.png` },
    { id: 'contact', name: 'open me', src: `${process.env.PUBLIC_URL}/icons/contacts.png` },
    { id: 'calendar', name: '', src: `${process.env.PUBLIC_URL}/icons/calendar.png` },
    { id: 'gta', name: '', src: `${process.env.PUBLIC_URL}/icons/gta-vicecity.png` },
    { id: 'reminders', name: 'open me', src: `${process.env.PUBLIC_URL}/icons/reminders.png` },
    { id: 'notes', name: '', src: `${process.env.PUBLIC_URL}/icons/notes.png` },
    { id: 'settings', name: '', src: `${process.env.PUBLIC_URL}/icons/settings.png` },
    { type: 'separator' },
    { id: 'terminal', name: 'open me', src: `${process.env.PUBLIC_URL}/icons/terminal.png` },
    { id: 'vscode', name: '', src: `${process.env.PUBLIC_URL}/icons/vscode.png` },
    { type: 'separator' },
    { id: 'foldercommon', name: '', src: `${process.env.PUBLIC_URL}/icons/foldercommon.png` },
    { id: 'trash', name: 'open me', src: `${process.env.PUBLIC_URL}/icons/trash.png` }
  ];

  const panelHeight = 68;
  const maxHeight = Math.max(100, 80 + 80/2 + 4);
  const heightRow = useTransform(isHovered, [0, 1], [panelHeight, maxHeight]);
  const height = useSpring(heightRow, { mass: 0.05, stiffness: 400, damping: 20 });

  return (
    <motion.div
      className="dock-outer"
      style={{ height }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.8, type: "spring", stiffness: 200, damping: 20 }}
    >
      <motion.div
        className="dock-panel"
        style={{ height: panelHeight }}
        onMouseMove={({ pageX }) => {
          isHovered.set(1);
          mouseX.set(pageX);
        }}
        onMouseLeave={() => {
          isHovered.set(0);
          mouseX.set(Infinity);
        }}
      >
        {dockItems.map((item, index) =>
          item.type === 'separator' ? (
            <div key={`sep-${index}`} className="dock-separator" />
          ) : (
            <DockItem
              key={item.id}
              item={item}
              onIconClick={onIconClick}
              mouseX={mouseX}
            />
          )
        )}
      </motion.div>
    </motion.div>
  );
};

// About Me Component (macOS-style text file)
const AboutMeContent = () => {
  return (
    <div style={{
      padding: '20px',
      fontFamily: 'SF Pro Text, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '14px',
      lineHeight: '1.6',
      color: '#333',
      backgroundColor: '#fff',
      height: 'calc(100% - 40px)',
      width: '100%',
      boxSizing: 'border-box',
      overflowY: 'auto'
    }}>
      <p style={{ margin: '0 0 16px 0' }}>
        Hey! I'm Ceren, a software engineer who loves turning ideas into digital experiences that feel alive. I get genuinely excited by solving tricky problems that feeds my adhd brain with challenges in code, UI quirks, or system design puzzles.
      </p>
      <p style={{ margin: '0 0 16px 0' }}>
        Every interaction, every pixel, every line of code gets my full attention; I obsess over the details that make a product not just functional, but a delight to use. My biggest motivation is making people go "wow."
      </p>
      <p style={{ margin: '0' }}>
        Beyond the cutesy creative, hands-on side of software engineering, I'm also passionate about{' '}
        <a 
          href="https://drive.google.com/file/d/1w90lKFx8bluuhAKU0NjO6wmvwtlqWDWN/view?usp=sharing" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#007AFF', textDecoration: 'underline' }}
        >
          Project Management
        </a>
        {' '}and{' '}
        <a 
          href="https://drive.google.com/file/d/1jYgaAGUh0ocDz-Aq_8niVIQQrya2o4iT/view?usp=sharing" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#007AFF', textDecoration: 'underline' }}
        >
          Machine Learning
        </a>
        .
      </p>
    </div>
  );
};

// Projects Content
const ProjectsContent = () => {
  const projects = [
    { 
      id: 1, 
      title: 'Project 01 (Whispqr)', 
      description: 'App for anonymous event feedback via QR codes with real-time messaging.',
      tech: 'React Native, Firebase, Expo'
    },
    { 
      id: 2, 
      title: 'Project 02 (Testly)', 
      description: 'Generates bilingual exam questions using GPT models.',
      tech: 'Next.js, TypeScript, Tailwind CSS, GitHub Models API'
    },
    { 
      id: 3, 
      title: 'Project 03 (Spotify Vibe Recommender)', 
      description: 'Streamlit app recommends songs using machine learning.',
      tech: 'Python, Streamlit, Scikit-learn, Plotly'
    },
    { 
      id: 4, 
      title: 'Project 04 (University Management System)', 
      description: 'Web-based university management system.',
      tech: 'PHP, MySQL, HTML, CSS, JavaScript'
    }
  ];

  return (
    <div className="projects-content">
      <h2>My Projects</h2>
      <div className="projects-grid">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            className="project-card-detailed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <h3>{project.title}</h3>
            <p>{project.description}</p>
            <div className="tech-stack">{project.tech}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Sticky Note Component
const StickyNote = () => {
  const todos = [
    { text: "Land a full time job", completed: false },
    { text: "Drink water", completed: false },
    { text: "Get a labubu", completed: false },
    { text: "Finish uni without losing my mind", completed: true },
    { text: "Feed the cats", completed: true },
    { text: "World domination", completed: false },
    { text: "Get really good at cooking", completed: true },
    { text: "Travel somewhere new every year", completed: false },
    { text: "Finish this portfolio", completed: false }
  ];

  return (
    <motion.div
      className="sticky-note"
      initial={{ opacity: 0, y: -20, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ delay: 1, duration: 0.8, type: "spring" }}
      whileHover={{ 
        rotate: 3, 
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
    >
      <div className="sticky-note-content">
        <h3 className="sticky-note-title">To do:</h3>
        <ul className="sticky-note-list">
          {todos.map((todo, index) => (
            <motion.li
              key={index}
              className={`sticky-note-item ${todo.completed ? 'completed' : ''}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 + index * 0.1 }}
            >
              {todo.text}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

// macOS Reminders Popup Component
const RemindersPopup = ({ onClose }) => {
  const [reminders, setReminders] = useState([
    { id: 1, text: 'Stay hydrated!!!', completed: false, time: 'Morning' },
  ]);

  const toggleReminder = (id) => {
    setReminders(reminders.map(reminder => 
      reminder.id === id ? { ...reminder, completed: !reminder.completed } : reminder
    ));
  };

  return (
    <div className="reminders-popup-overlay" onClick={onClose}>
      <div className="reminders-window" onClick={(e) => e.stopPropagation()}>
        <div className="reminders-titlebar">
          <div className="traffic-lights">
            <button className="traffic-light close" onClick={onClose}>
              <span></span>
            </button>
            <button className="traffic-light minimize">
              <span></span>
            </button>
            <button className="traffic-light maximize">
              <span></span>
            </button>
          </div>
        </div>
        
        <div className="reminders-content">
          <div className="reminders-header">
            <h2>Today</h2>
            <button className="add-reminder-btn">+</button>
          </div>
          
          <div className="reminders-list">
            {/* Morning Section */}
            <div className="time-section">Morning</div>
            {reminders.filter(r => r.time === 'Morning').map(reminder => (
              <div key={reminder.id} className="reminder-item">
                <div 
                  className={`reminder-checkbox ${reminder.completed ? 'completed' : ''}`}
                  onClick={() => toggleReminder(reminder.id)}
                >
                  {reminder.completed && '✓'}
                </div>
                <div className="reminder-content">
                  <div className={`reminder-text ${reminder.completed ? 'completed' : ''}`}>
                    {reminder.text}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Afternoon Section */}
            <div className="time-section">Afternoon</div>
            {reminders.filter(r => r.time === 'Afternoon').map(reminder => (
              <div key={reminder.id} className="reminder-item">
                <div 
                  className={`reminder-checkbox ${reminder.completed ? 'completed' : ''}`}
                  onClick={() => toggleReminder(reminder.id)}
                >
                  {reminder.completed && '✓'}
                </div>
                <div className="reminder-content">
                  <div className={`reminder-text ${reminder.completed ? 'completed' : ''}`}>
                    {reminder.text}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Tonight Section */}
            <div className="time-section">Tonight</div>
            {reminders.filter(r => r.time === 'Tonight').map(reminder => (
              <div key={reminder.id} className="reminder-item">
                <div 
                  className={`reminder-checkbox ${reminder.completed ? 'completed' : ''}`}
                  onClick={() => toggleReminder(reminder.id)}
                >
                  {reminder.completed && '✓'}
                </div>
                <div className="reminder-content">
                  <div className={`reminder-text ${reminder.completed ? 'completed' : ''}`}>
                    {reminder.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// React Bits Tilted Contact Card Component
const ContactCard = ({ onClose }) => {
  const ref = useRef(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const x = useMotionValue();
  const y = useMotionValue();
  const rotateX = useSpring(useMotionValue(0), { damping: 30, stiffness: 100, mass: 2 });
  const rotateY = useSpring(useMotionValue(0), { damping: 30, stiffness: 100, mass: 2 });
  const scale = useSpring(1, { damping: 30, stiffness: 100, mass: 2 });
  const opacity = useSpring(0);
  const rotateFigcaption = useSpring(0, { stiffness: 350, damping: 30, mass: 1 });

  const [lastY, setLastY] = useState(0);

  function handleMouse(e) {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;

    const rotationX = (offsetY / (rect.height / 2)) * -14;
    const rotationY = (offsetX / (rect.width / 2)) * 14;

    rotateX.set(rotationX);
    rotateY.set(rotationY);

    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);

    const velocityY = offsetY - lastY;
    rotateFigcaption.set(-velocityY * 0.6);
    setLastY(offsetY);
  }

  function handleMouseEnter() {
    scale.set(1.1);
    opacity.set(1);
  }

  function handleMouseLeave() {
    opacity.set(0);
    scale.set(1);
    rotateX.set(0);
    rotateY.set(0);
    rotateFigcaption.set(0);
  }

  return (
    <div className="contact-card-overlay" onClick={onClose}>
      <figure
        ref={ref}
        className="tilted-card-figure"
        style={{
          height: "400px",
          width: "600px",
        }}
        onMouseMove={handleMouse}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          className="tilted-card-inner"
          style={{
            width: "600px",
            height: "400px",
            rotateX,
            rotateY,
            scale,
          }}
        >
                     <div 
             className="tilted-card-contact-content"
             style={{
               backgroundImage: `url(${process.env.PUBLIC_URL}/phone.jpg)`,
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}
           >
             <div className="contact-header">
               <h2><ShinyText text="Get In Touch" disabled={false} speed={3} /></h2>
               <p>Let's build something cool.</p>
             </div>
             
             <div className="contact-details">
               <div className="contact-detail-item">
                 <span 
                   className="contact-email-address" 
                   onClick={() => {
                     const email = 'cerenerkayiran@gmail.com';
                     
                     // Simple fallback method that works everywhere
                     const textArea = document.createElement('textarea');
                     textArea.value = email;
                     textArea.style.position = 'fixed';
                     textArea.style.left = '-999999px';
                     textArea.style.top = '-999999px';
                     document.body.appendChild(textArea);
                     textArea.focus();
                     textArea.select();
                     
                     try {
                       document.execCommand('copy');
                       setShowCopiedMessage(true);
                       setTimeout(() => setShowCopiedMessage(false), 3000);
                     } catch (err) {
                       console.error('Failed to copy email:', err);
                     }
                     
                     document.body.removeChild(textArea);
                   }}
                   style={{ cursor: 'pointer' }}
                   title="Click to copy email"
                 >
                   cerenerkayiran@gmail.com
                 </span>
               </div>
               <div className="contact-detail-item">
                 <a 
                   href="https://www.linkedin.com/in/ceren-erkayiran/" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="contact-linkedin-link"
                 >
                   LinkedIn
                 </a>
               </div>
             </div>
           </div>
        </motion.div>


      </figure>

      {/* Professional Copy Success Message */}
      <AnimatePresence>
        {showCopiedMessage && (
          <motion.div
            className="copy-success-toast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            ✓ Email copied to clipboard
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// macOS Terminal Popup Component  
const TerminalPopup = ({ onClose }) => {
  const windowRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const position = useRef({ x: 0, y: 0 });

  // Format current date like
  const now = new Date();
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = n => n < 10 ? '0' + n : n;
  const day = days[now.getDay()];
  const month = months[now.getMonth()];
  const date = now.getDate();
  const hours = pad(now.getHours());
  const mins = pad(now.getMinutes());
  const secs = pad(now.getSeconds());
  const dateStr = `${day} ${month}  ${date < 10 ? ' ' : ''}${date} ${hours}:${mins}:${secs} on Earth`;

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current = {
      x: e.clientX - position.current.x,
      y: e.clientY - position.current.y
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (isDragging.current && windowRef.current) {
      position.current = {
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      };
      windowRef.current.style.transform = `translate(${position.current.x}px, ${position.current.y}px)`;
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  return (
    <div className="terminal-popup">
      <div 
        ref={windowRef}
        className="terminal-window"
      >
        <div 
          className="terminal-titlebar"
          onMouseDown={handleMouseDown}
          style={{ cursor: 'grab' }}
        >
          <div className="traffic-lights">
            <button className="traffic-light close" onClick={onClose}>
              <span></span>
            </button>
            <button className="traffic-light minimize">
              <span></span>
            </button>
            <button className="traffic-light maximize">
              <span></span>
            </button>
          </div>
          <div className="terminal-title">🫡 cerenerkayiran — -zsh — 80×24</div>
        </div>
        <div className="terminal-content">
          <div className="terminal-line">
            <span className="terminal-prompt">Last login: {dateStr}</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-prompt">cerenerkayiran@ ~ % </span>
            <span className="terminal-command">echo "hire me already ;)"</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-output">hire me already ;)</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-prompt">cerenerkayiran@ ~ % </span>
            <span className="terminal-cursor"></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// macOS Finder-style Window Component
const FinderWindow = ({ title, description, items, onClose, onItemClick, onSidebarClick, activeSidebarItem }) => {
  return (
    <div className="finder-window">
      <div className="finder-titlebar">
        <div className="traffic-lights">
          <button className="traffic-light close" onClick={onClose}>
            <span></span>
          </button>
          <button className="traffic-light minimize">
            <span></span>
          </button>
          <button className="traffic-light maximize">
            <span></span>
          </button>
        </div>
        <div className="finder-window-title-section">
          <div className="finder-window-title">{title}</div>
          {description && <div className="finder-window-description">{description}</div>}
        </div>
        <div className="titlebar-spacer"></div>
      </div>
      
      <div className="finder-content">
        <div className="finder-sidebar">
          <div className="sidebar-section">
            <div className="sidebar-header">Navigation</div>
            <div className={`sidebar-item ${activeSidebarItem === 'work' ? 'active' : ''}`} onClick={() => onSidebarClick('work')}>Work</div>
            <div className={`sidebar-item ${activeSidebarItem === 'resume' ? 'active' : ''}`} onClick={() => onSidebarClick('resume')}>Resume</div>
            <div className={`sidebar-item ${activeSidebarItem === 'trash' ? 'active' : ''}`} onClick={() => onSidebarClick('trash')}>Trash</div>
          </div>
          
          <div className="sidebar-section">
            <div className="sidebar-header">Projects</div>
            <div className={`sidebar-item ${activeSidebarItem === 'project1' ? 'active' : ''}`} onClick={() => onSidebarClick('project1')}>
              <MacIcon type="grey-folder" size={16} style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}} /> Project 01
            </div>
            <div className={`sidebar-item ${activeSidebarItem === 'project2' ? 'active' : ''}`} onClick={() => onSidebarClick('project2')}>
              <MacIcon type="grey-folder" size={16} style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}} /> Project 02
            </div>
            <div className={`sidebar-item ${activeSidebarItem === 'project3' ? 'active' : ''}`} onClick={() => onSidebarClick('project3')}>
              <MacIcon type="grey-folder" size={16} style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}} /> Project 03
            </div>
            <div className={`sidebar-item ${activeSidebarItem === 'project4' ? 'active' : ''}`} onClick={() => onSidebarClick('project4')}>
              <MacIcon type="grey-folder" size={16} style={{display: 'inline-block', marginRight: '8px', verticalAlign: 'middle'}} /> Project 04
            </div>
          </div>
        </div>
        
        <div className="finder-main">
          <div className="finder-items">
            {items.map((item, index) => (
              <div key={index} className="finder-item" onClick={() => onItemClick(item)}>
                <div className="finder-item-icon">
                  {item.icon && item.icon.includes('/') ? (
                    <img src={item.icon} alt={item.name} style={{ width: '64px', height: '64px' }} />
                  ) : item.icon && !item.icon.includes('📄') && !item.icon.includes('🌐') ? (
                    <MacIcon type={item.icon} size={64} />
                  ) : (
                    <span style={{ fontSize: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px' }}>{item.icon}</span>
                  )}
                </div>
                <div className="finder-item-name">{item.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Welcome Text Component
const WelcomeText = () => {
  const containerRef = useRef(null);

  return (
    <div 
      ref={containerRef}
      translate="no"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        zIndex: 5,
        pointerEvents: 'none'
      }}
    >
      <div style={{ marginBottom: '10px' }}>
        <VariableProximity
          label="welcome to my"
          className="welcome-small"
          fromFontVariationSettings="'wght' 300, 'opsz' 12"
          toFontVariationSettings="'wght' 600, 'opsz' 20"
          containerRef={containerRef}
          radius={80}
          falloff="linear"
          translate="no"
          style={{
            fontSize: '20px',
            color: 'rgba(0, 0, 0, 0.7)',
            letterSpacing: '0px'
          }}
        />
      </div>
      <div>
        <VariableProximity
          label="portfolio"
          className="welcome-large"
          fromFontVariationSettings="'wght' 500, 'opsz' 16"
          toFontVariationSettings="'wght' 800, 'opsz' 48"
          containerRef={containerRef}
          radius={120}
          falloff="exponential"
          translate="no"
          style={{
            fontSize: '50px',
            fontStyle: 'italic',
            color: 'rgba(0, 0, 0, 0.8)',
            letterSpacing: '-2px'
          }}
        />
      </div>
    </div>
  );
};

function App() {
  const [windows, setWindows] = useState([]);
  const [finderWindow, setFinderWindow] = useState({ isOpen: false, title: '', description: '', items: [], activeSidebarItem: '' });
  const [terminalPopup, setTerminalPopup] = useState({ isOpen: false });
  const [contactCard, setContactCard] = useState({ isOpen: false });
  const [remindersPopup, setRemindersPopup] = useState({ isOpen: false });
  const [photosGallery, setPhotosGallery] = useState({ isOpen: false });
  const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const orbRef = useRef();

  // Simple resize handler
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use a ref for drag constraints that actually work
  const desktopRef = useRef(null);
  
  const dragConstraints = useMemo(() => {
    if (!desktopRef.current) return {};
    
    const rect = desktopRef.current.getBoundingClientRect();
    const dockHeight = 120; // Approximate dock height
    
    return {
      top: 30, // Below menu bar
      bottom: rect.height - dockHeight - 80, // Above dock area
      left: 20, // Small margin from left edge
      right: rect.width - 20 // Small margin from right edge
    };
  }, [screenSize.width, screenSize.height]);

  // Desktop icons with original positions - optimized for performance
  const desktopIcons = useMemo(() => {
    const screenWidth = screenSize.width;
    const screenHeight = screenSize.height;
    
    // Original fixed positions
    const basePositions = [
      { id: 'project1', icon: 'folderwork', title: 'Project 01\n(Whispqr)', position: { top: 100, left: 1000 } },
      { id: 'project2', icon: 'folderwork', title: 'Project 02\n(Testly)', position: { top: 200, left: 1150 } },
      { id: 'project3', icon: 'folderwork', title: 'Project 03\n(Spotify Vibe Recommender)', position: { top: 350, left: 1150 } },
      { id: 'project4', icon: 'folderwork', title: 'Project 04\n(University Management System)', position: { top: 500, left: 1100 } },
      { id: 'aboutme', icon: 'txt', title: "About Me", position: { top: 350, left: 80 } },
      { id: 'resume', icon: 'document', title: 'Resume.pdf', position: { top: 500, left: 150 } },
    ];
    
    // Only adjust positions for smaller screens to prevent off-screen icons
    if (screenWidth < 1200) {
      return basePositions.map(icon => ({
        ...icon,
        position: {
          top: Math.min(icon.position.top, screenHeight - 300),
          left: Math.min(icon.position.left, screenWidth - 150)
        }
      }));
    }
    
    return basePositions;
  }, [screenSize.width, screenSize.height]);

  const openWindow = (windowConfig) => {
    // Close any existing window with the same id
    setWindows(prev => prev.filter(w => w.id !== windowConfig.id));
    
    const newWindow = {
      id: windowConfig.id || Date.now(),
      title: windowConfig.title,
      component: windowConfig.component,
      width: windowConfig.width,
      height: windowConfig.height,
      windowType: windowConfig.windowType,
      position: {
        top: windowConfig.id === 'about' ? 100 : Math.random() * 200 + 100,
        left: windowConfig.id === 'about' ? 300 : Math.random() * 300 + 200
      },
      isMinimized: false
    };
    setWindows(prev => [...prev, newWindow]);
  };

  const closeWindow = (id) => {
    setWindows(windows.filter(w => w.id !== id));
  };

  const minimizeWindow = (id) => {
    setWindows(windows.map(w => 
      w.id === id ? { ...w, isMinimized: !w.isMinimized } : w
    ));
  };

  const showFinderWindow = (title, description, items, activeSidebarItem = '') => {
    setFinderWindow({
      isOpen: true,
      title,
      description,
      items,
      activeSidebarItem
    });
  };

  const closeFinderWindow = () => {
    setFinderWindow({ isOpen: false, title: '', description: '', items: [], activeSidebarItem: '' });
  };

  const showTerminalPopup = () => {
    setTerminalPopup({ isOpen: true });
  };

  const closeTerminalPopup = () => {
    setTerminalPopup({ isOpen: false });
  };

  const showContactCard = () => {
    setContactCard({ isOpen: true });
  };

  const closeContactCard = () => {
    setContactCard({ isOpen: false });
  };

  const showRemindersPopup = () => {
    setRemindersPopup({ isOpen: true });
  };

  const closeRemindersPopup = () => {
    setRemindersPopup({ isOpen: false });
  };

  const showPhotosGallery = () => {
    setPhotosGallery({ isOpen: true });
  };

  const closePhotosGallery = () => {
    setPhotosGallery({ isOpen: false });
  };

  const handleResumeClick = () => {
    window.open('https://drive.google.com/file/d/1eO5-oymGbzKzp8hnXmicOtMmcoteNG1q/view?usp=sharing', '_blank');
  };



  const handleFinderItemClick = (item) => {
    if (item.action) {
      // Only close finder window if it's not an external link
      if (item.name !== 'GitHub Repository' && item.name !== 'Live Website') {
        closeFinderWindow();
      }
      item.action();
    }
  };

  const handleSidebarClick = (sidebarItem) => {
    const projectDescriptions = {
      'project1': 'App for anonymous event feedback via QR codes with real-time messaging.',
      'project2': 'Generates bilingual exam questions using GPT models.',
      'project3': 'Streamlit app recommends songs using machine learning.',
      'project4': 'Web-based university management system.'
    };

    switch(sidebarItem) {
      case 'work':
        // Show all projects together
        const allProjectItems = [
          { 
            icon: 'folderwork', 
            name: 'Project 01 (Whispqr)',
            action: () => {
              const project1Items = [
                { 
                  icon: 'written-file', 
                  name: 'GitHub Repository',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://github.com/cerenerkayiran/whispqr', '_blank');
                  }
                },
                { 
                  icon: 'player', 
                  name: 'Demo Video',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://drive.google.com/file/d/1YAdChkWexUwcJa0TbLnzR7ij_IO5hPQq/view?usp=sharing', '_blank');
                  }
                }
              ];
                             showFinderWindow('Project 01 (Whispqr)', projectDescriptions['project1'], project1Items, 'project1');
            }
          },
          { 
            icon: 'folderwork', 
            name: 'Project 02 (Testly)',
            action: () => {
              const project2Items = [
                { 
                  icon: 'written-file', 
                  name: 'GitHub Repository',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://github.com/cerenerkayiran/testly', '_blank');
                  }
                },
                { 
                  icon: 'safari', 
                  name: 'Live Website',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://testly-cyan.vercel.app/', '_blank');
                  }
                }
              ];
                             showFinderWindow('Project 02 (Testly)', projectDescriptions['project2'], project2Items, 'project2');
            }
          },
          { 
            icon: 'folderwork', 
            name: 'Project 03 (Spotify Vibe Recommender)',
            action: () => {
              const project3Items = [
                { 
                  icon: 'written-file', 
                  name: 'GitHub Repository',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://github.com/cerenerkayiran/spotify-vibe-recommender', '_blank');
                  }
                },
                { 
                  icon: 'safari', 
                  name: 'Live Website',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://spotify-vibe-recommender-cerenerkayiran.streamlit.app/', '_blank');
                  }
                }
              ];
                             showFinderWindow('Project 03 (Spotify Vibe Recommender)', projectDescriptions['project3'], project3Items, 'project3');
            }
          },
                      { 
              icon: 'folderwork', 
              name: 'Project 04 (University Management System)',
            action: () => {
              const project4Items = [
                { 
                  icon: 'written-file', 
                  name: 'GitHub Repository',
                  action: (e) => {
                    e?.stopPropagation();
                    window.open('https://github.com/cerenerkayiran/university-management-system', '_blank');
                  }
                }
              ];
                             showFinderWindow('Project 04 (University Management System)', projectDescriptions['project4'], project4Items, 'project4');
            }
          }
        ];
                 showFinderWindow('Work - All Projects', 'Recent projects', allProjectItems, 'work');
        break;
      
      case 'resume':
        showFinderWindow(
          'Resume',
          'View my resume',
          [
            {
              icon: 'written-file',
              name: 'Resume-2025.pdf',
              action: () => window.open('https://drive.google.com/file/d/1eO5-oymGbzKzp8hnXmicOtMmcoteNG1q/view?usp=sharing', '_blank')
            }
          ],
          'resume'
        );
        break;
      
      case 'trash':
        showFinderWindow(
          'Trash',
          'Items you delete are moved to the Trash',
          [
            {
              icon: 'written-file',
              name: 'World Domination Plan.docx'
            }
          ],
          'trash'
        );
        break;
      
      case 'project1':
      case 'project2':
      case 'project3':
      case 'project4':
        // Navigate to individual project (reuse existing logic)
        const projectMap = {
          'project1': { id: 'project1', title: 'Project 01\n(Whispqr)' },
          'project2': { id: 'project2', title: 'Project 02\n(Testly)' },
          'project3': { id: 'project3', title: 'Project 03\n(Spotify Vibe Recommender)' },
          'project4': { id: 'project4', title: 'Project 04\n(University Management System)' }
        };
        
        const selectedProject = projectMap[sidebarItem];
        const projectLinks = {
          'project1': [
            { 
              icon: 'written-file', 
              name: 'GitHub Repository',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://github.com/cerenerkayiran/whispqr', '_blank');
              }
            },
            { 
              icon: 'player', 
              name: 'Demo Video',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://drive.google.com/file/d/1YAdChkWexUwcJa0TbLnzR7ij_IO5hPQq/view?usp=sharing', '_blank');
              }
            }
          ],
          'project2': [
            { 
              icon: 'written-file', 
              name: 'GitHub Repository',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://github.com/cerenerkayiran/testly', '_blank');
              }
            },
            { 
              icon: 'safari', 
              name: 'Live Website',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://testly-cyan.vercel.app/', '_blank');
              }
            }
          ],
          'project3': [
            { 
              icon: 'written-file', 
              name: 'GitHub Repository',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://github.com/cerenerkayiran/spotify-vibe-recommender', '_blank');
              }
            },
            { 
              icon: 'safari', 
              name: 'Live Website',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://spotify-vibe-recommender-cerenerkayiran.streamlit.app/', '_blank');
              }
            }
          ],
          'project4': [
            { 
              icon: 'written-file', 
              name: 'GitHub Repository',
              action: (e) => {
                e?.stopPropagation();
                window.open('https://github.com/cerenerkayiran/university-management-system', '_blank');
              }
            }
          ]
        };
        
        const folderItems = projectLinks[sidebarItem] || [];
        
                 showFinderWindow(
                           `${selectedProject.title.replace('\n', ' ')}`, 
           projectDescriptions[sidebarItem], 
           folderItems,
           sidebarItem
         );
        break;
      
      default:
        break;
    }
  };

  const handleDockIconClick = (item, event) => {
    // Show Finder window for folder icons
    if (item.src.includes('folder-common.png') || item.src.includes('folder-work.png')) {
      let folderItems = [];
      
      if (item.id === 'about') {
        folderItems = [
          { 
            icon: 'written-file', 
            name: 'aboutme.txt',
            action: () => openWindow({ id: 'about', title: 'aboutme.txt', component: <AboutMeContent />, width: '350px', height: '450px', windowType: 'about' })
          }
        ];
      } else if (item.id === 'projects') {
        folderItems = [
          { 
            icon: 'grey-folder', 
            name: 'Project 01',
            action: () => openWindow({ id: 'project1', title: 'Project 01', component: <ProjectsContent /> })
          },
          { 
            icon: 'grey-folder', 
            name: 'Project 02',
            action: () => openWindow({ id: 'project2', title: 'Project 02', component: <ProjectsContent /> })
          },
          { 
            icon: 'grey-folder', 
            name: 'Project 03',
            action: () => openWindow({ id: 'project3', title: 'Project 03', component: <ProjectsContent /> })
          },
          { 
            icon: 'grey-folder', 
            name: 'Project 04',
            action: () => openWindow({ id: 'project4', title: 'Project 04', component: <ProjectsContent /> })
          }
        ];
      } else if (item.id === 'resume') {
        folderItems = [
          { 
            icon: 'written-file', 
            name: 'Resume-2025.pdf',
            action: () => window.open('https://drive.google.com/file/d/1eO5-oymGbzKzp8hnXmicOtMmcoteNG1q/view?usp=sharing', '_blank')
          }
        ];
      }
      
      showFinderWindow(`${item.name} - Visual Design & UI`, folderItems);
      return;
    }

    // Default behavior for non-folder icons
    switch(item.id) {
      case 'about':
        openWindow({
          id: 'about',
          title: 'aboutme.txt',
          component: <AboutMeContent />,
          width: '350px',
          height: '450px',
          windowType: 'about'
        });
        break;
      case 'projects':
        openWindow({
          id: 'projects',
          title: 'My Projects',
          component: <ProjectsContent />
        });
        break;
      case 'resume':
        window.open('https://drive.google.com/file/d/1eO5-oymGbzKzp8hnXmicOtMmcoteNG1q/view?usp=sharing', '_blank');
        break;
      case 'terminal':
        showTerminalPopup();
        return;
      case 'contact':
        showContactCard();
        return;
      case 'photos':
        showPhotosGallery();
        return;
      case 'reminders':
        showRemindersPopup();
        return;
      case 'trash':
        showFinderWindow(
          'Trash',
          'Items you delete are moved to the Trash',
          [
            {
              icon: 'written-file',
              name: 'World Domination Plan.docx'
            }
          ],
          'trash'
        );
        return;
      default:
        break;
    }
  };

  const handleDesktopIconClick = (icon, event) => {
    // Show Finder window for all project icons
    if (icon.id.startsWith('project')) {
      // Project descriptions
      const projectDescriptions = {
        'project1': 'App for anonymous event feedback via QR codes with real-time messaging.',
        'project2': 'Generates bilingual exam questions using GPT models.',
        'project3': 'Streamlit app recommends songs using machine learning.',
        'project4': 'Web-based university management system.'
      };

      const desktopProjectLinks = {
        'project1': [
          { 
            icon: 'written-file', 
            name: 'GitHub Repository',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://github.com/cerenerkayiran/whispqr', '_blank');
            }
          },
          { 
            icon: 'player', 
            name: 'Demo Video',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://drive.google.com/file/d/1YAdChkWexUwcJa0TbLnzR7ij_IO5hPQq/view?usp=sharing', '_blank');
            }
          }
        ],
        'project2': [
          { 
            icon: 'written-file', 
            name: 'GitHub Repository',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://github.com/cerenerkayiran/testly', '_blank');
            }
          },
          { 
            icon: 'safari', 
            name: 'Live Website',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://testly-cyan.vercel.app/', '_blank');
            }
          }
        ],
        'project3': [
          { 
            icon: 'written-file', 
            name: 'GitHub Repository',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://github.com/cerenerkayiran/spotify-vibe-recommender', '_blank');
            }
          },
          { 
            icon: 'safari', 
            name: 'Live Website',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://spotify-vibe-recommender-cerenerkayiran.streamlit.app/', '_blank');
            }
          }
        ],
        'project4': [
          { 
            icon: 'written-file', 
            name: 'GitHub Repository',
            action: (e) => {
              e?.stopPropagation();
              window.open('https://github.com/cerenerkayiran/university-management-system', '_blank');
            }
          }
        ]
      };
      
      const folderItems = desktopProjectLinks[icon.id] || [];
      
      showFinderWindow(
                        `${icon.title.replace('\n', ' ')}`, 
        projectDescriptions[icon.id], 
        folderItems,
        icon.id
      );
      return;
    }

    // Handle terminal icon click
    if (icon.id === 'terminal') {
      showTerminalPopup();
      return;
    }

    // Handle resume icon click
    if (icon.id === 'resume') {
      window.open('https://drive.google.com/file/d/1eO5-oymGbzKzp8hnXmicOtMmcoteNG1q/view?usp=sharing', '_blank');
      return;
    }

    // Handle aboutme icon click
    if (icon.id === 'aboutme') {
      openWindow({
        id: 'about',
        title: 'aboutme.txt',
        component: <AboutMeContent />,
        width: '350px',
        height: '450px',
        windowType: 'about'
      });
      return;
    }

    // Default behavior for other desktop icons
    // Add other desktop icon behaviors here if needed
  };

  return (
    <div className="macos-desktop" ref={desktopRef}>
      {/* Orb Animated Background */}
      <div style={{ width: '100vw', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none' }}>
        <Orb
          ref={orbRef}
          hoverIntensity={0.5}
          rotateOnHover={true}
          hue={0}
          forceHoverState={false}
        />
      </div>

      {/* macOS Menu Bar */}
      <MenuBar onContactClick={showContactCard} onResumeClick={handleResumeClick} />
      
      {/* Desktop Background */}
      <div
        className="desktop-background"
        onMouseMove={e => orbRef.current?.handleMouseMove(e)}
        onMouseLeave={e => orbRef.current?.handleMouseLeave(e)}
      >
        
        {/* Sticky Note */}
        <StickyNote />
        
        {/* Welcome Text */}
        <WelcomeText />
        
        {/* Desktop Icons */}
        <AnimatePresence>
          {desktopIcons.map((icon) => (
            <DesktopIcon
              key={icon.id}
              iconId={icon.id}
              icon={icon.icon}
              title={icon.title}
              position={icon.position}
              onClick={(e) => handleDesktopIconClick(icon, e)}
              dragConstraints={dragConstraints}
            />
          ))}
        </AnimatePresence>

        {/* Windows */}
        <AnimatePresence>
          {windows.map((window) => (
            <MacWindow
              key={window.id}
              title={window.title}
              initialPosition={window.position}
              onClose={() => closeWindow(window.id)}
              onMinimize={() => minimizeWindow(window.id)}
              isMinimized={window.isMinimized}
              width={window.width}
              height={window.height}
              windowType={window.windowType}
            >
              {window.component}
            </MacWindow>
          ))}
        </AnimatePresence>
      </div>

      {/* macOS Dock */}
      <Dock onIconClick={handleDockIconClick} />

      {/* macOS Finder Window */}
      {finderWindow.isOpen && (
        <motion.div
          className="finder-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <FinderWindow 
            title={finderWindow.title}
            description={finderWindow.description}
            items={finderWindow.items}
            onClose={closeFinderWindow}
            onItemClick={handleFinderItemClick}
            onSidebarClick={handleSidebarClick}
            activeSidebarItem={finderWindow.activeSidebarItem}
          />
        </motion.div>
      )}

      {/* Terminal Popup */}
      {terminalPopup.isOpen && (
        <motion.div
          className="terminal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
                  <TerminalPopup onClose={closeTerminalPopup} />
      </motion.div>
    )}

    {/* Contact Card */}
    {contactCard.isOpen && (
      <motion.div
        className="contact-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <ContactCard onClose={closeContactCard} />
      </motion.div>
    )}

    {/* Reminders Popup */}
    {remindersPopup.isOpen && (
      <motion.div
        className="reminders-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <RemindersPopup onClose={closeRemindersPopup} />
      </motion.div>
    )}

    {/* Photos Gallery */}
    {photosGallery.isOpen && (
      <motion.div
        className="photos-gallery-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div 
          className="photos-gallery-window"
          style={{ 
            width: '85vw', 
            height: '75vh', 
            position: 'relative',
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div className="mac-titlebar">
            <div className="traffic-lights">
              <button className="traffic-light close" onClick={closePhotosGallery}>
                <span></span>
              </button>
              <button className="traffic-light minimize">
                <span></span>
              </button>
              <button className="traffic-light maximize">
                <span></span>
              </button>
            </div>
            <div className="window-title">Say hi to my cats!</div>
            <div className="titlebar-spacer"></div>
          </div>
          <div style={{ height: 'calc(100% - 40px)', marginTop: '40px' }}>
            <CircularGallery 
              items={[
                { image: `${process.env.PUBLIC_URL}/cats/1 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/2 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/3 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/4 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/5 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/6 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/7 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/8 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/9 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/10 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/11 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/12 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/13 Medium.jpeg`, text: "" },
                { image: `${process.env.PUBLIC_URL}/cats/14 Medium.jpeg`, text: "" }
              ]}
              bend={1.5}
              textColor="#ffffff"
              borderRadius={0.05}
              scrollEase={0.02}
            />
          </div>
        </div>
      </motion.div>
    )}
  </div>
);
}

export default App;